/* Movatic WMS — Frontend | CesarFilho 932048016 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { useQuery } from '@tanstack/react-query'
import { useToast } from '@/components/Toast'

// ADR-0049 F2b: religa o botão Imprimir ao contrato REAL do backend.
// Fluxo (server-side, com auditoria): POST labels/{location|pallet}/ -> devolve ZPL
// -> POST print/ (cria PrintJob e envia via socket) usando host/port da impressora
// escolhida (F1a). As rotas antigas por-ID (/locations/{id}/print) nunca existiram.
type Printer = { id: string; name: string; host?: string; port?: number }
type TypeOpt = 'location' | 'pallet'

export default function PrintLabels() {
  const { show } = useToast()
  const [type, setType] = useState<TypeOpt>('location')
  // `code` = location_code (ex.: A-01-01) OU sscc do pallet, conforme o tipo.
  const [code, setCode] = useState('')
  const [productCode, setProductCode] = useState('') // labels/pallet/ exige product_code
  const [copies, setCopies] = useState(1)
  const [printer, setPrinter] = useState<string>('')
  const [busy, setBusy] = useState(false)

  // Sugestões (autocomplete)
  const [q, setQ] = useState('')
  const [openSug, setOpenSug] = useState(false)

  const { data: printers } = useQuery<Printer[]>({
    queryKey: ['printers'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/api/printing/printers/')
        // /printers/ é DRF paginado {count,results:[]}, não um array. Desembrulhar.
        const arr = Array.isArray(data) ? data : (data?.results ?? [])
        return arr as Printer[]
      } catch {
        return [
          { id: 'Zebra-01', name: 'Zebra-01' },
          { id: 'Zebra-02', name: 'Zebra-02' },
        ]
      }
    },
  })

  useEffect(() => {
    if (!printer && printers?.length) setPrinter(String(printers[0].id))
  }, [printers, printer])

  const suggestions = useQuery({
    queryKey: ['sug', type, q],
    enabled: q.trim().length >= 2,
    queryFn: async () => {
      try {
        if (type === 'location') {
          const { data } = await api.get('/api/core/locations/', {
            params: { search: q, page_size: 10 },
          })
          const arr = Array.isArray(data) ? data : (data.results ?? [])
          // ADR-0049 F2b: capturar o CODE (location_code), não o id numérico —
          // é o que labels/location/ espera.
          return arr.map((r: any) => ({
            value: String(r.code),
            label: `${r.code} — ${r.name ?? ''}`,
          }))
        } else {
          const { data } = await api.get('/api/inventory/stock-items/', {
            params: { pallet_code: q, page_size: 10 },
          })
          const arr = Array.isArray(data) ? data : (data.results ?? [])
          return arr.map((r: any) => ({
            value: String(r.sscc ?? r.pallet_code ?? r.id),
            label: `${r.pallet_code ?? r.sscc ?? r.id}`,
          }))
        }
      } catch {
        return [] // sem sugestões
      }
    },
  })

  const canPrint = useMemo(() => {
    if (!code.trim()) return false
    if (type === 'pallet' && !productCode.trim()) return false
    return true
  }, [code, productCode, type])

  const onPrint = async () => {
    if (!canPrint || busy) return
    setBusy(true)
    try {
      // 1) impressora escolhida -> host/port (F1a expõe-nos no /printers/).
      const sel = (printers ?? []).find((p) => String(p.id) === String(printer))

      // 2) render: labels/{tipo}/ -> ZPL (sem copies aqui; multiplicamos no print/).
      const labelUrl =
        type === 'location'
          ? '/api/printing/labels/location/'
          : '/api/printing/labels/pallet/'
      const context: Record<string, unknown> =
        type === 'location'
          ? { location_code: code.trim() }
          : { product_code: productCode.trim(), sscc: code.trim() }
      const { data: rendered } = await api.post(labelUrl, context)
      const zpl = rendered?.zpl
      if (!zpl) throw new Error('Falha ao gerar o ZPL da etiqueta.')

      // 3) print: print/ cria PrintJob (auditoria) e envia via socket para host/port.
      await api.post('/api/printing/print/', {
        zpl,
        copies,
        host: sel?.host,
        port: sel?.port,
        template: type === 'location' ? 'label.location' : 'label.sscc',
        context,
      })
      show({
        type: 'success',
        message: 'Enviado à fila de impressão (PrintJob criado).',
      })
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ?? e?.message ?? 'Falha ao enviar impressão'
      show({ type: 'error', message: msg })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card p-4 max-w-2xl">
      <h2 className="text-lg font-semibold">Impressão de Etiquetas</h2>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="label">Tipo</label>
          <select
            className="input"
            value={type}
            onChange={(e) => {
              setType(e.target.value as TypeOpt)
              setCode('')
              setProductCode('')
              setQ('')
            }}
          >
            <option value="location">Endereçamento (Location)</option>
            <option value="pallet">Palete/SSCC</option>
          </select>
        </div>
        <div>
          <label className="label">Impressora</label>
          <select
            className="input"
            value={printer}
            onChange={(e) => setPrinter(e.target.value)}
          >
            {(printers ?? []).map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Cópias</label>
          <input
            className="input"
            type="number"
            min={1}
            max={50}
            value={copies}
            onChange={(e) => setCopies(Number(e.target.value))}
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="label">
          {type === 'location' ? 'Código do endereço' : 'SSCC da palete'}
        </label>
        <input
          className={
            'input ' + (!canPrint && code ? 'ring-2 ring-red-500' : '')
          }
          placeholder={
            type === 'location'
              ? 'ex.: A-01-01 (location_code)'
              : 'ex.: 000000000000000000 (SSCC, 18 dígitos)'
          }
          value={code}
          onChange={(e) => {
            setCode(e.target.value)
            setQ(e.target.value)
            setOpenSug(true)
          }}
          onBlur={() => setTimeout(() => setOpenSug(false), 150)}
          onFocus={() => setOpenSug(true)}
        />
        {openSug && suggestions.data && suggestions.data.length > 0 && (
          <div className="mt-1 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-card bg-white dark:bg-neutral-900">
            {suggestions.data.map((s: any, i: number) => (
              <button
                key={i}
                className="w-full text-left px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => {
                  setCode(s.value)
                  setQ(s.label)
                  setOpenSug(false)
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {type === 'pallet' && (
        <div className="mt-4">
          <label className="label">Código do produto</label>
          <input
            className="input"
            placeholder="ex.: SKU-123 (product_code, obrigatório)"
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
          />
        </div>
      )}

      <button
        className="btn mt-4"
        onClick={onPrint}
        disabled={!canPrint || busy}
      >
        {busy ? 'A enviar…' : 'Imprimir'}
      </button>
    </div>
  )
}
